import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { Check, AlertCircle, X } from "lucide-react";

interface SmartInputProps {
  id: string;
  label: string;
  type?: "text" | "email" | "tel" | "date";
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

interface ValidationState {
  isValid: boolean;
  message: string;
  status: "idle" | "validating" | "valid" | "invalid";
}

export function SmartInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  required = false,
  placeholder,
  className = ""
}: SmartInputProps) {
  const [validation, setValidation] = useState<ValidationState>({
    isValid: !required || value.trim() !== "",
    message: required && value.trim() === "" ? `${label} is required` : "",
    status: required && value.trim() === "" ? "invalid" : "idle"
  });

  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const validate = async () => {
      setValidation(prev => ({ ...prev, status: "validating" }));

      let isValid = true;
      let message = "";

      // Required field validation
      if (required && !value.trim()) {
        isValid = false;
        message = `${label} is required`;
      }
      // Email validation
      else if (type === "email" && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          isValid = false;
          message = "Please enter a valid email address";
        } else {
          // Check for common typos
          const commonDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com"];
          const domain = value.split("@")[1]?.toLowerCase();
          if (domain && !commonDomains.includes(domain)) {
            const suggestions = commonDomains.filter(d => 
              d.includes(domain.substring(0, 3)) || domain.includes(d.substring(0, 3))
            );
            if (suggestions.length > 0) {
              message = `Did you mean @${suggestions[0]}?`;
            }
          }
        }
      }
      // Phone validation
      else if (type === "tel" && value) {
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        const cleanPhone = value.replace(/[\s\-\(\)]/g, "");
        if (!phoneRegex.test(cleanPhone)) {
          isValid = false;
          message = "Please enter a valid phone number";
        }
      }
      // Date validation
      else if (type === "date" && value) {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
          isValid = false;
          message = "Please select a future date";
        }
      }

      setValidation({
        isValid,
        message,
        status: isValid ? "valid" : "invalid"
      });
    };

    const debounceTimer = setTimeout(validate, 300);
    return () => clearTimeout(debounceTimer);
  }, [value, type, required, label, touched]);

  const handleBlur = () => {
    setTouched(true);
  };

  const getStatusIcon = () => {
    switch (validation.status) {
      case "validating":
        return <div className="animate-spin h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full" />;
      case "valid":
        return <Check className="h-4 w-4 text-success" />;
      case "invalid":
        return <X className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getInputClassName = () => {
    let baseClassName = className;
    
    if (validation.status === "valid") {
      baseClassName += " border-success focus-visible:ring-success";
    } else if (validation.status === "invalid") {
      baseClassName += " border-destructive focus-visible:ring-destructive";
    }
    
    return baseClassName;
  };

  // Use enhanced PhoneInput for telephone inputs
  if (type === "tel") {
    return (
      <PhoneInput
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={className}
      />
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      
      <div className="relative">
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={getInputClassName()}
          required={required}
        />
        
        {(validation.status === "validating" || validation.status === "valid" || validation.status === "invalid") && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {getStatusIcon()}
          </div>
        )}
      </div>
      
      {validation.message && (
        <div className={`flex items-center gap-2 text-sm ${
          validation.status === "invalid" ? "text-destructive" : "text-muted-foreground"
        }`}>
          {validation.status === "invalid" && <AlertCircle className="h-4 w-4" />}
          <span>{validation.message}</span>
        </div>
      )}
    </div>
  );
}